/**
 * Story 054 — TanStack Query hooks for the sites feature.
 *
 * Per `tanstack-query-conventions` skill: hierarchical keys + per-query
 * `staleTime` + zero retries on mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { assetsFetch } from "./assetsFetch.js";
import { sitesKeys } from "./keys.js";
import type {
  CreateSiteBody,
  ListSitesParams,
  ListSitesResponse,
  ReverseGeocodeCountryResponse,
  SiteDetail,
  UpdateSiteBody,
} from "./types.js";

/**
 * Translate FE-internal `sort=key:dir` into BE wire format
 * (`key` for asc, `-key` for desc — assets/v1@1.0.0 §listSites).
 */
function sortToBeWire(sort: string | undefined): string | undefined {
  if (!sort) return undefined;
  const [k, d] = sort.split(":");
  if (!k) return undefined;
  if (d === "desc") return `-${k}`;
  return k;
}

function listSitesUrl(params: ListSitesParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  // FE state field is `search`; BE wire param is `q` (trigram ILIKE).
  if (params.search && params.search.length > 0) {
    sp.set("q", params.search);
  }
  const beSort = sortToBeWire(params.sort);
  if (beSort) sp.set("sort", beSort);
  // FE state field is `includeInactive`; BE wire param is
  // `include_deactivated`.
  if (params.includeInactive === true) sp.set("include_deactivated", "true");
  // BE wire only accepts a single `country_code`. When the FE state
  // carries multiple selected codes we send the first; the others stay
  // visually selected but are not enforced server-side. Tracked as a
  // BE follow-up (multi-value support) — until then the multi-select
  // UI degrades gracefully to single-country filtering.
  if (params.country && params.country.length > 0) {
    const sorted = [...params.country].sort((a, b) => a.localeCompare(b));
    if (sorted[0]) sp.set("country_code", sorted[0]);
  }
  // Tri-state offshore — only emit when explicitly true or false.
  if (params.offshore === true) sp.set("is_offshore", "true");
  else if (params.offshore === false) sp.set("is_offshore", "false");
  return `/sites?${sp.toString()}`;
}

interface BeSite {
  id: string;
  siteCode: number;
  siteName: string;
  countryCode?: string | null;
  isOffshore: boolean;
  assetCount: number;
  deactivatedAt?: string | null;
  createdAt: string;
  updatedAt?: string;
}

interface BeSiteList {
  items: BeSite[];
  total: number;
  page: number;
  pageSize: number;
  /** Present once the BE ships FEAT-010 envelope support (tenant-wide
   *  distinct countryCode set). Until then the FE derives it client-side
   *  from `items` — narrower than the true tenant set, but a strict
   *  subset of it (and the only thing computable from the wire today). */
  availableCountries?: string[] | null;
}

/**
 * Translate the BE wire payload (`assets/v1@1.0.0` SiteList) into the
 * FE-internal `ListSitesResponse` shape used by the page:
 *   - `deactivatedAt` (BE) → derived `isActive` + legacy `deletedAt` mirror
 *   - `availableCountries` envelope (FE-only) computed from items, since
 *     the BE doesn't emit it
 *   - `createdBy: null` placeholder (BE only sends it on SiteDetail)
 */
function mapBeListResponseToFe(be: BeSiteList): ListSitesResponse {
  const items = be.items.map((r) => ({
    id: r.id,
    siteCode: r.siteCode,
    siteName: r.siteName,
    countryCode: r.countryCode ?? null,
    isOffshore: r.isOffshore,
    assetCount: r.assetCount,
    createdAt: r.createdAt,
    createdBy: null,
    deletedAt: r.deactivatedAt ?? null,
    isActive: !r.deactivatedAt,
  }));
  const availableCountries =
    Array.isArray(be.availableCountries) && be.availableCountries.length > 0
      ? [...be.availableCountries].sort((a, b) => a.localeCompare(b))
      : Array.from(
          new Set(
            items
              .map((s) => s.countryCode)
              .filter(
                (c): c is string => typeof c === "string" && c.length > 0,
              ),
          ),
        ).sort((a, b) => a.localeCompare(b));
  return {
    items,
    total: be.total,
    page: be.page,
    pageSize: be.pageSize,
    availableCountries,
  };
}

export function useSitesListQuery(params: ListSitesParams) {
  return useQuery<ListSitesResponse>({
    queryKey: sitesKeys.list(params),
    queryFn: async () => {
      const be = await assetsFetch<BeSiteList>(listSitesUrl(params));
      return mapBeListResponseToFe(be);
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Server-side typeahead for the Park-filter Autocomplete on the
 * Turbines page. Issues a debounced `?q=<input>` against `listSites`
 * (trigram ILIKE, indexed). Returns up to 50 hits per call — enough
 * to drive a dropdown without paginating mid-typeahead.
 *
 * `enabled` gates on a non-empty query so a freshly-opened dropdown
 * doesn't fire a request. Pass the user's already-debounced input.
 */
export function useSitesSearchQuery(query: string) {
  const trimmed = query.trim();
  return useQuery<ListSitesResponse>({
    queryKey: [...sitesKeys.lists(), "__search__", trimmed] as const,
    queryFn: async () => {
      const be = await assetsFetch<BeSiteList>(
        `/sites?page=1&pageSize=50&sort=siteName&q=${encodeURIComponent(trimmed)}`,
      );
      return mapBeListResponseToFe(be);
    },
    enabled: trimmed.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Fetch the **complete** tenant site catalogue (all pages aggregated).
 *
 * Used today by the Add/Edit Turbine modal Park picker only — modals
 * are explicit user actions, opened rarely; the 5-minute cache keeps
 * the cost off the hot path. Filter dropdowns on hot list pages
 * (e.g. Turbines Park filter) use `useSitesSearchQuery` instead so
 * we never download every site on page load.
 *
 * Filed as a BE follow-up in FEAT-010 to add a `?compact=true`
 * projection that drops the BE pageSize cap; on adoption this hook
 * becomes a single roundtrip instead of N parallel calls.
 */
export function useAllSitesQuery() {
  return useQuery<ListSitesResponse>({
    queryKey: [...sitesKeys.lists(), "__all__"] as const,
    queryFn: async () => {
      const PAGE_SIZE = 200;
      const first = await assetsFetch<BeSiteList>(
        `/sites?page=1&pageSize=${String(PAGE_SIZE)}&sort=siteName`,
      );
      if (first.total <= PAGE_SIZE) {
        return mapBeListResponseToFe(first);
      }
      const totalPages = Math.ceil(first.total / PAGE_SIZE);
      const restPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          assetsFetch<BeSiteList>(
            `/sites?page=${String(i + 2)}&pageSize=${String(PAGE_SIZE)}&sort=siteName`,
          ),
        ),
      );
      const allItems = [first.items, ...restPages.map((p) => p.items)].flat();
      return mapBeListResponseToFe({
        items: allItems,
        total: first.total,
        page: 1,
        pageSize: allItems.length,
      });
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Map the BE `SiteDetail` wire shape (deactivatedAt + deactivatedBy)
 * into the FE-internal `SiteDetail` (deletedAt + deletedBy + isActive).
 * `updatedBy`/`deletedBy` arrive as `{ userId, displayName }` and the
 * FE renders them via the `id`/`displayName` keys — so rename `userId`.
 */
function mapBeUserRef(
  r: { userId: string; displayName?: string | null } | null | undefined,
): { id: string; displayName: string } | null {
  if (!r) return null;
  return { id: r.userId, displayName: r.displayName ?? "" };
}

function mapBeDetailToFe(be: Record<string, unknown>): SiteDetail {
  const deactivatedAt = be.deactivatedAt as string | null | undefined;
  return {
    id: be.id as string,
    siteCode: be.siteCode as number,
    siteName: be.siteName as string,
    countryCode: (be.countryCode as string | null | undefined) ?? null,
    isOffshore: be.isOffshore as boolean,
    aviationAuthority:
      (be.aviationAuthority as string | null | undefined) ?? null,
    controlCenterPhoneNumber:
      (be.controlCenterPhoneNumber as string | null | undefined) ?? null,
    additionalDescription:
      (be.additionalDescription as string | null | undefined) ?? null,
    latitude: (be.latitude as number | null | undefined) ?? null,
    longitude: (be.longitude as number | null | undefined) ?? null,
    commissionedAt: (be.commissionedAt as string | null | undefined) ?? null,
    externalIds:
      (be.externalIds as { provider: string; id: string }[] | undefined) ?? [],
    createdAt: be.createdAt as string,
    createdBy: mapBeUserRef(
      be.createdBy as { userId: string; displayName?: string | null },
    ),
    updatedAt: be.updatedAt as string,
    updatedBy: mapBeUserRef(
      be.updatedBy as { userId: string; displayName?: string | null },
    ),
    deletedAt: deactivatedAt ?? null,
    deletedBy: mapBeUserRef(
      be.deactivatedBy as
        | { userId: string; displayName?: string | null }
        | null
        | undefined,
    ),
    isActive: !deactivatedAt,
    assetCount: (be.assetCount as number | undefined) ?? 0,
  };
}

export function useSiteDetailQuery(id: string | null | undefined) {
  return useQuery<SiteDetail>({
    queryKey: sitesKeys.detail(id ?? "__none__"),
    queryFn: async () => {
      const be = await assetsFetch<Record<string, unknown>>(
        `/sites/${id ?? ""}`,
      );
      return mapBeDetailToFe(be);
    },
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useCreateSiteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSiteBody) => {
      const be = await assetsFetch<Record<string, unknown>>("/sites", {
        method: "POST",
        body,
      });
      return mapBeDetailToFe(be);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sitesKeys.lists() });
    },
  });
}

export interface UpdateSiteArgs {
  id: string;
  body: UpdateSiteBody;
}

export function useUpdateSiteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: UpdateSiteArgs) => {
      const be = await assetsFetch<Record<string, unknown>>(`/sites/${id}`, {
        method: "PATCH",
        body,
      });
      return mapBeDetailToFe(be);
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: sitesKeys.lists() });
      void qc.invalidateQueries({ queryKey: sitesKeys.detail(updated.id) });
    },
  });
}

export function useDeactivateSiteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const be = await assetsFetch<Record<string, unknown>>(`/sites/${id}`, {
        method: "DELETE",
      });
      return mapBeDetailToFe(be);
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: sitesKeys.lists() });
      void qc.invalidateQueries({ queryKey: sitesKeys.detail(updated.id) });
    },
  });
}

export function useReactivateSiteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const be = await assetsFetch<Record<string, unknown>>(
        `/sites/${id}/reactivate`,
        { method: "POST" },
      );
      return mapBeDetailToFe(be);
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: sitesKeys.lists() });
      void qc.invalidateQueries({ queryKey: sitesKeys.detail(updated.id) });
    },
  });
}

export interface GeocodeArgs {
  lat: number;
  lon: number;
}

export function useGeocodeMutation() {
  return useMutation({
    mutationFn: ({ lat, lon }: GeocodeArgs) =>
      assetsFetch<ReverseGeocodeCountryResponse>(
        `/geocode/country-from-coords?lat=${String(lat)}&lon=${String(lon)}`,
      ),
  });
}
